import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, DeviceMobile, Bank, Wallet, LockKey, CheckCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { startCashfreeCheckout } from "../lib/cashfree";
import ManualUPICheckout from "../components/ManualUPICheckout";
import { Button } from "../components/ui/button";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const METHODS = [
  { id: "upi", label: "UPI", desc: "GPay, PhonePe, Paytm, BHIM", icon: DeviceMobile },
  { id: "card", label: "Credit / Debit Card", desc: "Visa, Mastercard, RuPay", icon: CreditCard },
  { id: "netbanking", label: "Netbanking", desc: "All major banks", icon: Bank },
  { id: "wallet", label: "Wallets", desc: "Paytm, Mobikwik, Amazon Pay", icon: Wallet },
];

export default function RentalCheckoutPage() {
  const { rentalId } = useParams();
  const nav = useNavigate();
  const [rental, setRental] = useState(null);
  const [rz, setRz] = useState(null);
  const [method, setMethod] = useState("upi");
  const [upiId, setUpiId] = useState("");
  const [processing, setProcessing] = useState(false);
  const [orderError, setOrderError] = useState(null);

  useEffect(() => {
    api.get(`/rentals/${rentalId}`).then(({ data }) => setRental(data));
    api.post(`/payments/create-order/rental/${rentalId}`)
      .then(({ data }) => setRz(data))
      .catch((e) => setOrderError(formatApiError(e.response?.data?.detail) || "Payment gateway unavailable"));
  }, [rentalId]);

  const pay = async (preferUpi = false) => {
    if (!rz) return;
    setProcessing(true);

    // Cashfree branch
    if (rz.provider === "cashfree") {
      await startCashfreeCheckout({
        kind: "rental",
        refId: rentalId,
        onFail: () => setProcessing(false),
      });
      return; // browser redirects to Cashfree
    }

    // Razorpay branch
    if (!window.Razorpay) {
      toast.error("Payment gateway not loaded. Please refresh and try again.");
      setProcessing(false);
      return;
    }

    const options = {
      key: rz.key_id,
      amount: rz.amount,
      currency: rz.currency,
      name: "Zyro",
      description: `Rental ${rental.property_title}`,
      order_id: rz.order_id,
      prefill: {
        name: rental.user_name,
        email: rental.user_email,
      },
      theme: { color: "#111827" },
      handler: async (res) => {
        try {
          await api.post("/payments/verify", {
            kind: "rental", ref_id: rentalId,
            razorpay_order_id: res.razorpay_order_id,
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_signature: res.razorpay_signature,
          });
          toast.success("Payment successful — rental confirmed!");
          nav("/my-rentals");
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || "Verification failed");
        } finally { setProcessing(false); }
      },
      modal: {
        ondismiss: () => setProcessing(false),
      },
    };

    if (preferUpi) {
      options.config = {
        display: {
          blocks: {
            upi: { name: "Pay using UPI (recommended)", instruments: [{ method: "upi" }] },
          },
          sequence: ["block.upi"],
          preferences: { show_default_blocks: true },
        },
      };
    }

    const razorpay = new window.Razorpay(options);
    razorpay.open();
  };

  if (!rental) return <div className="min-h-[50vh] flex items-center justify-center"><div className="w-10 h-10 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12" data-testid="rental-checkout-page">
      <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">Almost there</p>
      <h1 className="font-serif text-4xl text-midnight mb-10">Pay rent & deposit</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-4">
          {orderError && (
            <div className="p-6 bg-red-50 border border-red-200 rounded-2xl" data-testid="rental-payment-error">
              <p className="text-xs tracking-widest uppercase text-red-700 mb-2">⚠️ Payment gateway not ready</p>
              <p className="text-sm text-red-900">{orderError}</p>
              <p className="text-xs text-red-700 mt-3">Please try again in a few minutes, or contact conect@zyroapp.in.</p>
            </div>
          )}
          {rz?.provider === "manual_upi" ? (
            <ManualUPICheckout
              order={rz}
              kind="rental"
              refId={rentalId}
              entityStatus={rental?.manual_upi_status || "awaiting_proof"}
              onSubmitted={() => api.get(`/rentals/${rentalId}`).then(({ data }) => setRental(data))}
            />
          ) : (
            <>
          <p className="text-xs tracking-widest uppercase text-neutral-500 mb-2">Choose payment method</p>
          <RadioGroup value={method} onValueChange={setMethod} className="space-y-3">
            {METHODS.map((m) => {
              const Icon = m.icon;
              return (
                <label key={m.id} htmlFor={`pm-${m.id}`}
                  className={`flex items-center gap-4 p-5 rounded-2xl border cursor-pointer transition-colors ${
                    method === m.id ? "border-gold bg-gold/5" : "border-black/10 hover:bg-stone"}`}
                  data-testid={`pm-${m.id}`}>
                  <RadioGroupItem value={m.id} id={`pm-${m.id}`} />
                  <Icon size={28} weight="duotone" className="text-gold" />
                  <div className="flex-1"><p className="text-midnight font-medium">{m.label}</p><p className="text-xs text-neutral-500">{m.desc}</p></div>
                </label>
              );
            })}
          </RadioGroup>
          {method === "upi" && (
            <div className="mt-4 p-5 bg-white border border-black/10 rounded-2xl">
              <Label className="text-xs tracking-widest uppercase text-neutral-500 mb-2 block">Enter UPI ID</Label>
              <Input value={upiId} onChange={(e) => setUpiId(e.target.value)} placeholder="yourname@upi" data-testid="upi-input" />
            </div>
          )}
            </>
          )}
        </div>

        <aside>
          <div className="sticky top-28 bg-white border border-black/10 rounded-2xl p-6">
            <h3 className="font-serif text-xl mb-4 text-midnight">Rental Summary</h3>
            {rental.property_image && <img src={rental.property_image} alt="" className="w-full h-32 object-cover rounded-xl mb-4" />}
            <p className="font-medium text-midnight mb-1">{rental.property_title}</p>
            <p className="text-xs text-neutral-500 mb-4">{rental.property_city}</p>

            <div className="space-y-2 text-sm border-y border-black/10 py-4 mb-4">
              <div className="flex justify-between text-neutral-600"><span>Move-in</span><span>{new Date(rental.start_date).toLocaleDateString("en-IN")}</span></div>
              <div className="flex justify-between text-neutral-600"><span>Lease</span><span>{rental.lease_months} months</span></div>
              <div className="flex justify-between text-neutral-600"><span>Rent × {rental.lease_months}</span><span>₹{rental.rent_total.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between text-neutral-600"><span>Deposit</span><span>₹{rental.security_deposit.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between text-neutral-600"><span>Platform fee</span><span>₹{rental.platform_fee.toLocaleString("en-IN")}</span></div>
            </div>
            <div className="flex justify-between mb-6">
              <span className="font-serif text-lg">Total</span>
              <span className="font-serif text-2xl text-midnight" data-testid="rental-checkout-total">₹{rental.total_amount.toLocaleString("en-IN")}</span>
            </div>
            {rz?.provider !== "manual_upi" && (
              <>
                <Button onClick={() => pay(true)} disabled={processing || rental.payment_status === "paid"} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6" data-testid="pay-upi-btn">
                  {rental.payment_status === "paid" ? (<span className="flex items-center gap-2"><CheckCircle size={18} weight="fill" /> Paid</span>) :
                    processing ? "Opening UPI..." : (<span className="flex items-center gap-2"><DeviceMobile size={18} weight="fill" /> Pay ₹{rental.total_amount.toLocaleString("en-IN")} via UPI</span>)}
                </Button>
                <Button onClick={() => pay(false)} disabled={processing || rental.payment_status === "paid"} variant="outline" className="w-full mt-2 border-midnight text-midnight hover:bg-stone" data-testid="pay-other-btn">
                  <LockKey size={14} className="mr-2" /> Choose another method
                </Button>
                {rz && (
                  <p className="text-[11px] text-center text-emerald-700 mt-3">
                    🔒 UPI · Cards · Netbanking · Wallets · Razorpay
                  </p>
                )}
              </>
            )}
            {rz?.provider === "manual_upi" && (
              <p className="text-[11px] text-center text-emerald-700 mt-3">
                💚 Direct UPI · Verified within 2-4 hrs
              </p>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
